import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase'; 

const ProgressMonitor = ({ tenantId }) => {
  const [students, setStudents] = useState([]);
  const [goalBank, setGoalBank] = useState({}); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form State
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGoal, setSelectedGoal] = useState('');
  const [selectedObjective, setSelectedObjective] = useState('');
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Data State
  const [percentage, setPercentage] = useState('');
  const [notes, setNotes] = useState('');
  
  // App State
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [existingLogId, setExistingLogId] = useState(null);
  const [logHistory, setLogHistory] = useState([]); 

  // Helper to safely check if current goal requires an objective
  const checkRequiresObjective = (category, goal, bank) => {
    const rawData = bank[category]?.[goal];
    return Array.isArray(rawData) && rawData.length > 0;
  };

  // 1. Initial Load: Fetch Students (WITH THE IA FIX)
  useEffect(() => {
    let isMounted = true; 
    const fetchStudents = async () => {
      try {
        if (!auth?.currentUser) return;
        const userRef = doc(db, 'districts', tenantId, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          if (isMounted) setError("User profile not found.");
          if (isMounted) setLoading(false);
          return;
        }
        const profile = userSnap.data();

        const studentsRef = collection(db, 'districts', tenantId, 'students');
        let studentQuery;
        
        if (profile?.role === 'Case Manager') {
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", auth.currentUser.uid));
        } else if (profile?.role === 'Instructional Assistant') {
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", profile.assignedCaseManager));
        } else if (profile?.role === 'ECE Recorder') {
          studentQuery = query(studentsRef, where("schoolId", "==", profile.schoolId));
        } else {
          studentQuery = query(studentsRef); 
        }
        
        const studentDocs = await getDocs(studentQuery);
        const loadedStudents = studentDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (isMounted) setStudents(loadedStudents.sort((a, b) => (a.name || a.studentName || '').localeCompare(b.name || b.studentName || '')));
      } catch (err) {
        console.error("Error fetching students:", err);
        if (isMounted) setError("Failed to load database.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchStudents();
    return () => { isMounted = false; };
  }, [tenantId]);

  // 2. Fetch Specific Student's Custom Goals when selected
  useEffect(() => {
    const fetchStudentGoals = async () => {
      if (!selectedStudentId) {
        setGoalBank({});
        setSelectedCategory('');
        setSelectedGoal('');
        setSelectedObjective('');
        return;
      }
      try {
        const studentRef = doc(db, 'districts', tenantId, 'students', selectedStudentId);
        const snap = await getDoc(studentRef);
        if (snap.exists()) {
          setGoalBank(snap.data().customGoals || {});
        } else {
          setGoalBank({});
        }
      } catch (err) {
        console.error("Error fetching individualized goals:", err);
      }
    };
    fetchStudentGoals();
  }, [selectedStudentId, tenantId]);

  // 3. THE LISTENER: Check for existing data on the SELECTED DATE
  useEffect(() => {
    const checkForExistingLog = async () => {
      const requiresObjective = checkRequiresObjective(selectedCategory, selectedGoal, goalBank);
      
      if (!selectedStudentId || !selectedGoal || !logDate || (requiresObjective && !selectedObjective)) {
        setExistingLogId(null);
        return;
      }

      setSaveMessage('Checking records...');
      try {
        const q = query(
          collection(db, 'districts', tenantId, 'students', selectedStudentId, 'progressLogs'),
          where('date', '==', logDate),
          where('goalText', '==', selectedGoal),
          where('objectiveText', '==', selectedObjective || 'Primary Goal') 
        );
        const snap = await getDocs(q);

        if (!snap.empty) {
          const existingData = snap.docs[0].data();
          setPercentage(existingData.percentage ?? '');
          setNotes(existingData.notes ?? '');
          setExistingLogId(snap.docs[0].id);
          setSaveMessage('');
        } else {
          setPercentage('');
          setNotes('');
          setExistingLogId(null);
          setSaveMessage('');
        }
      } catch (err) {
        console.error("Error checking existing logs:", err);
        setSaveMessage('');
      }
    };

    checkForExistingLog();
  }, [selectedStudentId, selectedCategory, selectedGoal, selectedObjective, logDate, tenantId, goalBank]);

  // 4. THE HISTORY FETCH: Pull ALL previous logs for the selected objective
  useEffect(() => {
    const fetchHistory = async () => {
      const requiresObjective = checkRequiresObjective(selectedCategory, selectedGoal, goalBank);
      
      if (!selectedStudentId || !selectedGoal || (requiresObjective && !selectedObjective)) {
        setLogHistory([]);
        return;
      }

      try {
        const q = query(
          collection(db, 'districts', tenantId, 'students', selectedStudentId, 'progressLogs'),
          where('goalText', '==', selectedGoal),
          where('objectiveText', '==', selectedObjective || 'Primary Goal')
        );
        const snap = await getDocs(q);
        
        const historyData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        historyData.sort((a, b) => new Date(b.date) - new Date(a.date));
        setLogHistory(historyData);
      } catch (err) {
        console.error("Error fetching history:", err);
      }
    };

    fetchHistory();
  }, [selectedStudentId, selectedCategory, selectedGoal, selectedObjective, tenantId, goalBank, isSaving]); 

  const handleSubmit = async () => {
    const requiresObjective = checkRequiresObjective(selectedCategory, selectedGoal, goalBank);

    if (!selectedStudentId || !selectedCategory || !selectedGoal || !logDate) {
      alert("Please ensure Student, Category, Goal, and Date are selected.");
      return;
    }
    
    if (requiresObjective && !selectedObjective) {
      alert("This goal requires an Objective to be selected.");
      return;
    }

    if (percentage === '' || isNaN(percentage)) {
      alert("Please enter a valid numerical percentage.");
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const logId = existingLogId || Date.now().toString(); 
      const logRef = doc(db, 'districts', tenantId, 'students', selectedStudentId, 'progressLogs', logId);
      
      await setDoc(logRef, {
        date: logDate,
        timestamp: new Date().toISOString(),
        category: selectedCategory,
        goalText: selectedGoal,
        objectiveText: requiresObjective ? selectedObjective : 'Primary Goal', 
        percentage: Number(percentage),
        notes: notes,
        recordedBy: auth.currentUser.uid
      });

      setSaveMessage('Progress Logged Successfully!');
      setExistingLogId(logId); 

      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error("Error saving log:", error);
      setSaveMessage('Error saving data.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>Loading Progress Monitor...</div>;
  if (error) return <div style={{ padding: '20px', color: '#ba0c2f', fontFamily: 'sans-serif' }}>{error}</div>;

  const safeGoalBankKeys = Object.keys(goalBank || {});
  const safeStudents = students || [];
  const availableGoals = selectedCategory && goalBank[selectedCategory] ? Object.keys(goalBank[selectedCategory]) : [];
  
  // Safely extract objectives as an array, defaulting to empty if none exist
  const rawObjectives = selectedCategory && selectedGoal ? goalBank[selectedCategory]?.[selectedGoal] : [];
  const availableObjectives = Array.isArray(rawObjectives) ? rawObjectives : [];

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      <div style={{ borderBottom: '2px solid #003366', paddingBottom: '10px', marginBottom: '25px' }}>
        <h2 style={{ margin: 0, color: '#003366', fontSize: '28px' }}>Progress Monitor</h2>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Classroom IEP Goal Tracking (Individualized)</p>
      </div>

      <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ flex: '1 1 250px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Select Student</label>
            <select 
              value={selectedStudentId} 
              onChange={(e) => setSelectedStudentId(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px', backgroundColor: '#f9f9f9' }}
            >
              <option value="">-- Choose Student --</option>
              {safeStudents.map(s => (
                <option key={s.id} value={s.id}>{s.name || s.studentName || s.fullName || 'Unknown Student'}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Date</label>
            <input 
              type="date" 
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              style={{ width: '100%', padding: '9px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px', backgroundColor: '#f9f9f9', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </div>

      {selectedStudentId && safeGoalBankKeys.length === 0 && (
        <div style={{ padding: '20px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '8px', border: '1px solid #ffeeba' }}>
          <strong>No IEP Goals Found!</strong> The Case Manager needs to set up this student's individualized goals in the <em>Caseload Manager</em> app before progress can be tracked.
        </div>
      )}

      {selectedStudentId && safeGoalBankKeys.length > 0 && (
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Target Selection</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#666', fontSize: '13px' }}>1. Goal Domain</label>
              <select 
                value={selectedCategory} 
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedGoal(''); 
                  setSelectedObjective('');
                }}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px' }}
              >
                <option value="">-- Select Domain --</option>
                {safeGoalBankKeys.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {selectedCategory && (
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#666', fontSize: '13px' }}>2. Specific IEP Goal</label>
                <select 
                  value={selectedGoal} 
                  onChange={(e) => {
                    setSelectedGoal(e.target.value);
                    setSelectedObjective(''); 
                  }}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px' }}
                >
                  <option value="">-- Select Goal --</option>
                  {availableGoals.map((goal, idx) => (
                    <option key={idx} value={goal}>{goal}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedGoal && availableObjectives.length > 0 && (
              <div style={{ padding: '15px', backgroundColor: '#fdf4f5', borderRadius: '6px', borderLeft: '4px solid #ba0c2f' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#ba0c2f', fontSize: '13px' }}>3. Target Objective (Required)</label>
                <select 
                  value={selectedObjective} 
                  onChange={(e) => setSelectedObjective(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ba0c2f', fontSize: '15px' }}
                >
                  <option value="">-- Select Objective --</option>
                  {availableObjectives.map((obj, idx) => (
                    <option key={idx} value={obj}>{obj}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedGoal && (availableObjectives.length === 0 || selectedObjective !== '') && (
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#ba0c2f' }}>Data Entry</h3>
            {existingLogId ? (
              <span style={{ backgroundColor: '#e6f0fa', color: '#003366', padding: '5px 10px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>✏️ Viewing/Editing Saved Log</span>
            ) : (
              <span style={{ backgroundColor: '#f4f4f4', color: '#666', padding: '5px 10px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>📄 Creating New Log</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
            <div style={{ flex: '1' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Percentage Score (%)</label>
              <input 
                type="number" 
                min="0"
                max="100"
                placeholder="e.g., 85"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                style={{ width: '100%', padding: '15px', borderRadius: '6px', border: '2px solid #003366', fontSize: '24px', textAlign: 'center', boxSizing: 'border-box', fontWeight: 'bold' }}
              />
            </div>
            <div style={{ flex: '2' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Session Notes (Optional)</label>
              <textarea 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Completed with minimum prompting."
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc', minHeight: '62px', boxSizing: 'border-box', fontFamily: 'sans-serif' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '20px', marginBottom: '20px' }}>
            <span style={{ color: saveMessage.includes('Error') ? '#ba0c2f' : (saveMessage === 'Checking records...' ? '#666' : '#28a745'), fontWeight: 'bold' }}>{saveMessage}</span>
            <button 
              onClick={handleSubmit}
              disabled={isSaving}
              style={{ padding: '12px 25px', backgroundColor: '#003366', color: '#fff', border: 'none', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '16px' }}
            >
              {isSaving ? 'Logging Data...' : (existingLogId ? 'Update Progress' : 'Save Progress')}
            </button>
          </div>

          {logHistory.length > 0 && (
            <div style={{ backgroundColor: '#fcfcfc', borderRadius: '8px', border: '1px solid #eee', padding: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#555', fontSize: '14px', textTransform: 'uppercase' }}>Recent History for this Objective</h4>
              <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '10px' }}>
                {logHistory.map(log => (
                  <div key={log.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                    <span style={{ fontWeight: 'bold', color: '#333', minWidth: '100px' }}>{log.date}</span>
                    <span style={{ color: log.percentage >= 80 ? '#28a745' : (log.percentage >= 60 ? '#f0ad4e' : '#ba0c2f'), fontWeight: '900', fontSize: '16px', minWidth: '60px' }}>{log.percentage}%</span>
                    <span style={{ color: '#888', fontSize: '13px', fontStyle: 'italic', flex: '1', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.notes || 'No notes'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressMonitor;